/**
 * ID Normalization Utilities
 * Ensures all IDs follow the format: {type}_{identifier}
 *
 * This module provides consistent ID formatting across the codebase.
 * All IDs should be prefixed with their object type for clarity and consistency.
 */
// Standard prefixes for all ID types
export const ID_PREFIXES = {
    slide: 'slide_',
    layout: 'layout_',
    master: 'master_',
    component: 'component_',
    shape: 'shape_',
    asset: 'asset_',
    deck: 'deck_',
    pres: 'pres_',
    job: 'job_',
};
/**
 * Normalize an ID to include the proper prefix.
 *
 * @param idValue - Raw ID value (may or may not have prefix)
 * @param idType - Type of ID ('slide', 'layout', 'master', etc.)
 * @returns Normalized ID with proper prefix (e.g., "slide_257")
 *
 * @example
 * normalizeId("257", "slide") // "slide_257"
 * normalizeId("slide_257", "slide") // "slide_257"
 * normalizeId("257#0", "slide") // "slide_257" (cleans hash suffix)
 * normalizeId(257, "slide") // "slide_257" (handles numbers)
 */
export function normalizeId(idValue, idType) {
    if (idValue === null || idValue === undefined) {
        return '';
    }
    let idStr = String(idValue);
    // Clean any hash suffix (e.g., "257#0" -> "257")
    if (idStr.includes('#')) {
        idStr = idStr.split('#')[0];
    }
    const prefix = ID_PREFIXES[idType] || `${idType}_`;
    // Already has correct prefix
    if (idStr.startsWith(prefix)) {
        return idStr;
    }
    // Has a different prefix - replace it
    for (const existingPrefix of Object.values(ID_PREFIXES)) {
        if (idStr.startsWith(existingPrefix)) {
            idStr = idStr.substring(existingPrefix.length);
            break;
        }
    }
    return `${prefix}${idStr}`;
}
/**
 * Extract the raw ID value from a normalized ID.
 *
 * @param normalizedId - ID with prefix (e.g., "slide_257")
 * @returns Raw ID value (e.g., "257")
 *
 * @example
 * extractRawId("slide_257") // "257"
 * extractRawId("layout_2147483952") // "2147483952"
 * extractRawId("257") // "257" (already raw)
 */
export function extractRawId(normalizedId) {
    if (!normalizedId) {
        return '';
    }
    for (const prefix of Object.values(ID_PREFIXES)) {
        if (normalizedId.startsWith(prefix)) {
            return normalizedId.substring(prefix.length);
        }
    }
    return normalizedId;
}
/**
 * Get the type of a normalized ID.
 *
 * @param normalizedId - ID with prefix
 * @returns ID type or null if not recognized
 *
 * @example
 * getIdType("slide_257") // "slide"
 * getIdType("component_headerSection_abc12") // "component"
 * getIdType("257") // null
 */
export function getIdType(normalizedId) {
    if (!normalizedId) {
        return null;
    }
    for (const [idType, prefix] of Object.entries(ID_PREFIXES)) {
        if (normalizedId.startsWith(prefix)) {
            return idType;
        }
    }
    return null;
}
/**
 * Check if an ID is already normalized with a known prefix.
 *
 * @param idValue - ID to check
 * @returns True if the ID has a recognized prefix
 *
 * @example
 * isNormalized("slide_257") // true
 * isNormalized("257") // false
 */
export function isNormalized(idValue) {
    if (!idValue) {
        return false;
    }
    return Object.values(ID_PREFIXES).some(prefix => idValue.startsWith(prefix));
}
/**
 * Clean an ID by removing hash suffixes without adding a prefix.
 * Use this when you just need to clean the raw value.
 *
 * @param idValue - Raw ID value
 * @returns Cleaned ID value without hash suffix
 *
 * @example
 * cleanId("257#0") // "257"
 * cleanId(257) // "257"
 */
export function cleanId(idValue) {
    if (idValue === null || idValue === undefined) {
        return '';
    }
    let idStr = String(idValue);
    if (idStr.includes('#')) {
        idStr = idStr.split('#')[0];
    }
    return idStr;
}
// Convenience functions for common ID types
/** Normalize a slide ID */
export function slideId(rawId) {
    return normalizeId(rawId, 'slide');
}
/** Normalize a layout ID */
export function layoutId(rawId) {
    return normalizeId(rawId, 'layout');
}
/** Normalize a master template ID */
export function masterId(rawId) {
    return normalizeId(rawId, 'master');
}
/** Normalize a component ID */
export function componentId(rawId) {
    return normalizeId(rawId, 'component');
}
/** Normalize a shape ID */
export function shapeId(rawId) {
    return normalizeId(rawId, 'shape');
}
/** Normalize an asset ID */
export function assetId(rawId) {
    return normalizeId(rawId, 'asset');
}
/** Normalize a deck ID */
export function documentId(rawId) {
    return normalizeId(rawId, 'deck');
}
